import React, { useState, useEffect } from 'react';
import ThemeToggle from '../ThemeToggle';
import { toast } from 'react-toastify';

function LoginPage({ onLogin, loginError }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (loginError) {
      setError(loginError);
      toast.error(loginError);
    }
  }, [loginError]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username || !password) {
      const errorMsg = 'Username and password are required.';
      setError(errorMsg);
      toast.error(errorMsg);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const success = await onLogin({ username, password });
      if (!success) {
        throw new Error('Login failed');
      }
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-brand">
          <div className="login-monogram">S</div>
          <span className="login-brand-text">SyncStatus</span>
        </div>
        <ThemeToggle />
      </div>

      <div className="login-content">
        <div className="login-card">
          <div className="login-title-section">
            <h1 className="login-heading">Welcome Back</h1>
            <p className="login-subheading">Sign in to the Sync Monitor dashboard</p>
          </div>

          {error && (
            <div className="login-error">
              <i className="bi bi-exclamation-circle-fill"></i>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label className="login-label" htmlFor="login-username">Username</label>
              <div className="login-input-wrap">
                <i className="bi bi-person login-input-icon"></i>
                <input
                  id="login-username"
                  type="text"
                  className="login-input"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoFocus
                  disabled={loading}
                />
              </div>
            </div>

            <div className="login-field">
              <label className="login-label" htmlFor="login-password">Password</label>
              <div className="login-input-wrap">
                <i className="bi bi-lock login-input-icon"></i>
                <input
                  id="login-password"
                  type="password"
                  className="login-input"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="schedule-spinner" aria-hidden="true"></span>
                  Signing in...
                </>
              ) : (
                <>
                  <i className="bi bi-arrow-right"></i>
                  Sign In
                </>
              )}
            </button>
          </form>

          <div className="login-footer-text">
            <p>&copy; {new Date().getFullYear()} SyncStatus &middot; XandY Learning</p>
          </div>
        </div>
      </div>

      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background-color: var(--bg-primary);
          color: var(--text-primary);
          font-family: var(--font-body);
        }

        .login-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 24px 32px;
        }

        .login-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .login-monogram {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background-color: var(--text-primary);
          color: var(--bg-primary);
          font-family: var(--font-display);
          font-size: 1.25rem;
          font-weight: 900;
          display: flex;
          align-items: center;
          justify-content: center;
          line-height: 1;
        }

        .login-brand-text {
          font-family: var(--font-display);
          font-size: 1.25rem;
          font-weight: 800;
          color: var(--text-primary);
          letter-spacing: 0.02em;
        }

        .login-content {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 24px;
        }

        .login-card {
          background-color: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: var(--radius-card);
          padding: 48px 40px;
          width: 100%;
          max-width: 440px;
          box-shadow: var(--shadow-card);
        }

        .login-title-section {
          text-align: center;
          margin-bottom: 36px;
        }

        .login-heading {
          font-family: var(--font-display);
          font-size: 1.75rem;
          font-weight: 900;
          color: var(--text-primary);
          margin: 0 0 8px;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .login-subheading {
          font-size: 0.875rem;
          color: var(--text-muted);
          margin: 0;
        }

        .login-error {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          border-radius: var(--radius-md);
          background-color: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.25);
          color: var(--accent-red);
          font-size: 0.875rem;
          margin-bottom: 24px;
        }

        .login-field {
          margin-bottom: 20px;
        }

        .login-label {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--text-secondary);
          margin-bottom: 6px;
        }

        .login-input-wrap {
          position: relative;
        }

        .login-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        .login-input {
          width: 100%;
          padding: 12px 14px 12px 40px;
          border: 1px solid var(--border);
          border-radius: var(--radius-md);
          background-color: var(--bg-secondary);
          color: var(--text-primary);
          font-size: 0.9375rem;
          font-family: var(--font-body);
          outline: none;
          transition: border-color 0.2s ease, background-color 0.2s ease;
        }

        .login-input:focus {
          border-color: var(--accent-green);
          background-color: var(--bg-card);
        }

        .login-input::placeholder {
          color: var(--text-muted);
        }

        .login-input:disabled {
          opacity: 0.6;
        }

        .login-submit {
          width: 100%;
          padding: 12px 20px;
          margin-top: 8px;
          border: none;
          border-radius: var(--radius-pill);
          background-color: var(--accent-green);
          color: #0f1a00;
          font-size: 0.9375rem;
          font-weight: 700;
          font-family: var(--font-body);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: opacity 0.2s ease, transform 0.1s ease;
        }

        .login-submit:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .login-submit:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .login-footer-text {
          margin-top: 32px;
          text-align: center;
        }

        .login-footer-text p {
          font-size: 0.75rem;
          color: var(--text-muted);
          margin: 0;
        }

        @media (max-width: 480px) {
          .login-card {
            padding: 36px 24px;
          }
          .login-header {
            padding: 16px 20px;
          }
        }
      `}</style>
    </div>
  );
}

export default LoginPage;
